// Normalisation du statut des lignes du wizard "Ajouter des comptes" face au
// modèle de tarification du type de compte choisi.
//
// Module PUR : testable sans monter TradingAccountsPage.tsx (tire
// wasp/client/operations → casse sous vitest, cf. tradovateImportCost.ts).
//
// POURQUOI. Un direct funded n'a jamais de phase de challenge (refusé par le
// serveur en 400) : le wizard bascule donc "challenge" -> "pa" dès que
// `directFunded` devient vrai. Deux angles morts trouvés en revue (Task 14) :
//
//  (a) LA BASCULE ÉTAIT À SENS UNIQUE. Revenir sur un type one_time après être
//      passé par un type direct funded laissait les lignes à "pa" : un compte
//      qui aurait dû être en challenge se retrouvait créé FUNDED, avec
//      `fundedAt` posé à maintenant, en silence.
//  (b) LES LIGNES RÉINITIALISÉES NE PASSAIENT PAS PAR LA BASCULE. `handleKindChange`
//      recrée les lignes via EMPTY_ROW (statut "challenge" par défaut) sans
//      repasser par l'effet qui les corrige : si `directFunded` est déjà vrai
//      au moment du reset (ex: Topstep X dont le template verrouillé n'a qu'un
//      type direct funded), la ligne neuve garde "challenge" et se fait
//      refuser à la soumission.
//
// ⚠️ PIÈGE REJOUÉ EN CORRIGEANT (b) : un premier correctif pré-basculait la
// ligne neuve dans `handleKindChange` lui-même, sur la valeur de `directFunded`
// lue À CET ENDROIT — qui est encore celle d'AVANT le reset (React n'a pas
// recalculé le rendu avec le nouveau kind/template). En repartant de Tradovate
// (direct funded) vers Topstep X (pas direct funded), la ligne neuve était donc
// switchée à "pa" à tort, ET son id n'était jamais ajouté à `autoSwitchedIds` :
// quand l'effet finissait par constater `directFunded === false`, il ne
// trouvait rien à réhabiliter. Un compte voulu en "challenge" se retrouvait
// créé FUNDED (`fundedAt` = maintenant), en silence.
//
// Le correctif retenu est délibérément simple : ne JAMAIS pré-basculer dans
// `handleKindChange` (la ligne neuve garde le défaut natif de EMPTY_ROW) et
// laisser les DEUX filets ci-dessous, qui lisent tous deux `directFunded` déjà
// RECALCULÉ pour le nouveau kind, faire le travail : la bascule réversible AU
// CHANGEMENT (couvre (a), et (b) dès que `directFunded` change de valeur au
// rendu suivant le reset) et la normalisation À LA SOUMISSION (filet
// systématique, couvre aussi (b) dans le cas où `directFunded` était déjà vrai
// avant le reset et le reste — l'effet ne se redéclenche alors pas faute de
// changement de valeur, mais aucune ligne "challenge" ne peut atteindre le
// serveur).

import type { AccountStatus } from "./accountStatus";

/**
 * Normalisation appliquée à la SOUMISSION : dernier filet avant l'appel
 * serveur, quel que soit le chemin qui a produit la ligne (reset de kind,
 * duplication de ligne, changement de type de compte...).
 */
export function normalizeStatusForSubmit(
  status: AccountStatus,
  directFunded: boolean,
): AccountStatus {
  return directFunded && status === "challenge" ? "pa" : status;
}

/**
 * Bascule réversible appliquée quand `directFunded` change de valeur.
 *
 * - `directFunded` passe à `true` : toute ligne "challenge" devient "pa", et
 *   son id est ajouté à `autoSwitchedIds` pour pouvoir revenir en arrière.
 * - `directFunded` passe à `false` : toute ligne dont l'id est dans
 *   `autoSwitchedIds` ET qui est toujours à "pa" (l'utilisateur ne l'a pas
 *   changée entre-temps) revient à "challenge". Les lignes que l'utilisateur a
 *   choisies explicitement (jamais ajoutées à `autoSwitchedIds`, ou changées
 *   depuis) ne sont JAMAIS touchées.
 *
 * Retourne les nouvelles lignes ET le nouvel ensemble d'ids auto-basculés (à
 * conserver côté appelant, typiquement dans une ref).
 */
export function toggleDirectFundedRows<R extends { id: string; status: AccountStatus }>(
  rows: R[],
  directFunded: boolean,
  autoSwitchedIds: ReadonlySet<string>,
): { rows: R[]; autoSwitchedIds: Set<string> } {
  if (directFunded) {
    const nextAutoSwitched = new Set(autoSwitchedIds);
    const nextRows = rows.map((r) => {
      if (r.status !== "challenge") return r;
      nextAutoSwitched.add(r.id);
      return { ...r, status: "pa" as AccountStatus };
    });
    return { rows: nextRows, autoSwitchedIds: nextAutoSwitched };
  }

  // Retour à un modèle qui autorise le challenge : ne réhabilite que ce que
  // CETTE bascule avait elle-même posé, et seulement si rien ne l'a changé
  // depuis (un choix explicite de l'utilisateur doit survivre).
  if (autoSwitchedIds.size === 0) return { rows, autoSwitchedIds: new Set() };
  const nextRows = rows.map((r) =>
    autoSwitchedIds.has(r.id) && r.status === "pa" ? { ...r, status: "challenge" as AccountStatus } : r,
  );
  return { rows: nextRows, autoSwitchedIds: new Set() };
}

/**
 * Propage le suivi "auto-basculée" à une ligne dupliquée depuis une autre.
 *
 * "Ajouter un compte" copie le statut de la dernière ligne (donc "pa" si elle
 * a été auto-basculée) mais génère un id neuf pour la copie : sans ce suivi,
 * la copie hérite du statut "pa" mais pas de l'entrée dans `autoSwitchedIds`,
 * et un retour à un type qui autorise le challenge ne la réhabiliterait jamais
 * (son id resterait introuvable dans l'ensemble).
 */
export function propagateAutoSwitched(
  autoSwitchedIds: ReadonlySet<string>,
  sourceRowId: string,
  newRowId: string,
): Set<string> {
  if (!autoSwitchedIds.has(sourceRowId)) return new Set(autoSwitchedIds);
  return new Set(autoSwitchedIds).add(newRowId);
}
